import React, { useEffect, useMemo, useState } from "react";
import { scopeSequenceAPI } from "../services/api";
import TEKSModal from "./TEKSModal";
import "./ScopeSequenceViewer.css";

const toDate = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const dateLike = raw.includes("T") ? raw.split("T")[0] : raw;
  const parsed = new Date(`${dateLike}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const formatDate = (value) => {
  const parsed = toDate(value);
  if (!parsed) return "TBD";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const toAssetUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = process.env.PUBLIC_URL || "";
  return `${base}${url}`;
};

const ScopeSequenceViewer = ({
  courseId,
  buttonLabel = "Scope & Sequence",
  buttonClassName = "primary-btn",
  selectedUnitNumber = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState("");
  const [activeSchoolYear, setActiveSchoolYear] = useState("");
  const [selectedTeksCode, setSelectedTeksCode] = useState(null);

  useEffect(() => {
    if (!isOpen || !courseId) return;

    let cancelled = false;
    const loadScopeSequence = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await scopeSequenceAPI.getAll(courseId);
        if (cancelled) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        setEntries(rows);

        const schoolYears = [...new Set(rows.map((row) => row.school_year).filter(Boolean))]
          .sort()
          .reverse();
        setActiveSchoolYear((prev) => (prev && schoolYears.includes(prev) ? prev : (schoolYears[0] || "")));
      } catch (loadError) {
        if (cancelled) return;
        console.error("Error loading scope and sequence entries:", loadError);
        setEntries([]);
        setError("Unable to load scope and sequence entries.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadScopeSequence();
    return () => {
      cancelled = true;
    };
  }, [courseId, isOpen]);

  const schoolYears = useMemo(
    () =>
      [...new Set(entries.map((entry) => entry.school_year).filter(Boolean))]
        .sort()
        .reverse(),
    [entries],
  );

  const visibleEntries = useMemo(() => {
    const filtered = activeSchoolYear
      ? entries.filter((entry) => entry.school_year === activeSchoolYear)
      : entries;

    return [...filtered].sort((a, b) => {
      const startA = String(a.start_date || "");
      const startB = String(b.start_date || "");
      if (startA !== startB) return startA.localeCompare(startB);
      return Number(a.unit_number || 0) - Number(b.unit_number || 0);
    });
  }, [activeSchoolYear, entries]);

  const sourceDocuments = useMemo(() => {
    const seen = new Set();
    const docs = [];

    visibleEntries.forEach((entry) => {
      const key = `${entry.source_document_title || ""}::${entry.source_document_url || ""}`;
      if (!entry.source_document_url || seen.has(key)) return;
      seen.add(key);
      docs.push({
        title: entry.source_document_title || "Source document",
        url: toAssetUrl(entry.source_document_url),
      });
    });

    return docs;
  }, [visibleEntries]);

  const normalizedBandRows = (entry, band) => {
    if (!Array.isArray(entry.normalized_teks)) return [];
    return entry.normalized_teks.filter((item) => item.band === band);
  };

  const renderBandChips = (entry, band, fallbackList) => {
    const normalized = normalizedBandRows(entry, band);

    if (normalized.length > 0) {
      return normalized.map((item) => {
        const codeToOpen = item.matched_teks_code || item.teks_code;
        return (
          <button
            key={`${entry.id}-${band}-${item.teks_code}-${item.source_token}`}
            type="button"
            className="scope-chip scope-chip-btn"
            onClick={() => setSelectedTeksCode(codeToOpen)}
            title={item.source_token ? `From ${item.source_token}` : undefined}
          >
            {item.teks_code}
          </button>
        );
      });
    }

    if (Array.isArray(fallbackList) && fallbackList.length > 0) {
      return fallbackList.map((code) => (
        <span key={`${entry.id}-${band}-${code}`} className="scope-chip">
          {code}
        </span>
      ));
    }

    return <span className="scope-chip muted">None listed</span>;
  };

  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => setIsOpen(true)}
        disabled={!courseId}
      >
        {buttonLabel}
      </button>

      {isOpen && (
        <div className="scope-sequence-modal" onClick={() => setIsOpen(false)}>
          <div className="scope-sequence-panel" onClick={(e) => e.stopPropagation()}>
            <div className="scope-sequence-header">
              <div>
                <h3>Scope & Sequence Viewer</h3>
                <p>Big ideas, TEKS bands, and pacing windows by unit.</p>
              </div>
              <button
                type="button"
                className="scope-close-btn"
                onClick={() => setIsOpen(false)}
                aria-label="Close scope and sequence viewer"
              >
                Close
              </button>
            </div>

            <div className="scope-sequence-controls">
              <div className="scope-meta">
                <span>{visibleEntries.length} entries</span>
              </div>
              {schoolYears.length > 1 && (
                <label className="scope-school-year-filter" htmlFor="scope-school-year">
                  School Year
                  <select
                    id="scope-school-year"
                    value={activeSchoolYear}
                    onChange={(e) => setActiveSchoolYear(e.target.value)}
                  >
                    {schoolYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            {sourceDocuments.length > 0 && (
              <div className="scope-doc-links">
                {sourceDocuments.map((doc) => (
                  <a key={doc.url} href={doc.url} target="_blank" rel="noreferrer">
                    View Source: {doc.title}
                  </a>
                ))}
              </div>
            )}

            <div className="scope-sequence-content">
              {loading && <p>Loading scope and sequence entries...</p>}
              {!loading && error && <p>{error}</p>}
              {!loading && !error && visibleEntries.length === 0 && (
                <p>No scope and sequence entries found for this course.</p>
              )}

              {!loading && !error && visibleEntries.length > 0 && (
                <div className="scope-sequence-cards">
                  {visibleEntries.map((entry) => {
                    const isSelected =
                      selectedUnitNumber !== null
                      && Number(entry.unit_number) === Number(selectedUnitNumber);

                    const readiness = Array.isArray(entry.teks_readiness_codes)
                      ? entry.teks_readiness_codes
                      : [];
                    const supporting = Array.isArray(entry.teks_supporting_codes)
                      ? entry.teks_supporting_codes
                      : [];
                    const processing = Array.isArray(entry.teks_processing_codes)
                      ? entry.teks_processing_codes
                      : [];

                    return (
                      <article
                        key={entry.id || `${entry.unit_number}-${entry.start_date}-${entry.end_date}`}
                        className={`scope-entry-card ${isSelected ? "is-selected" : ""}`}
                      >
                        <div className="scope-entry-header">
                          <div>
                            <div className="scope-unit-label">Unit {entry.unit_number}</div>
                            <h4>{entry.unit_title}</h4>
                          </div>
                          <div className="scope-date-chip">
                            {formatDate(entry.start_date)} - {formatDate(entry.end_date)}
                          </div>
                        </div>

                        <div className="scope-entry-submeta">
                          {(entry.nine_weeks_label || entry.semester_label) && (
                            <span>
                              {entry.nine_weeks_label}
                              {entry.nine_weeks_label && entry.semester_label ? " | " : ""}
                              {entry.semester_label}
                            </span>
                          )}
                          {typeof entry.ab_day_count === "number" && (
                            <span>A/B Days: {entry.ab_day_count}</span>
                          )}
                        </div>

                        <p className="scope-big-idea">{entry.big_idea}</p>

                        <div className="scope-teks-bands">
                          <div>
                            <strong>Readiness</strong>
                            <div className="scope-chip-row">
                              {renderBandChips(entry, "readiness", readiness)}
                            </div>
                          </div>

                          <div>
                            <strong>Supporting</strong>
                            <div className="scope-chip-row">
                              {renderBandChips(entry, "supporting", supporting)}
                            </div>
                          </div>

                          <div>
                            <strong>Processing</strong>
                            <div className="scope-chip-row">
                              {renderBandChips(entry, "processing", processing)}
                            </div>
                          </div>
                        </div>

                        {entry.notes && <p className="scope-notes">{entry.notes}</p>}
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedTeksCode && (
        <TEKSModal
          teksCode={selectedTeksCode}
          courseId={courseId}
          onClose={() => setSelectedTeksCode(null)}
        />
      )}
    </>
  );
};

export default ScopeSequenceViewer;
